import '../TrialLicenses/index.css';
import './License.css';

import { useEffect, useState, useMemo } from "react";
import axios from 'axios';
import { Modal, Button, Form, Dropdown } from "react-bootstrap";
import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import Loading from 'react-fullscreen-loading';
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import DataTable from 'react-data-table-component';
import ShowTime from "../../../utils/ShowTime/ShowTime";
import MapProduct from "../../../utils/ProductMapper/MapProduct";
import { FaEdit, FaLaptopMedical, FaRegIdCard, FaRegClock, FaEllipsisV, FaEye, FaRedo, FaTerminal, FaEnvelope } from "react-icons/fa";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import countryList from 'react-select-country-list';
import Select from 'react-select'
import CustomLoader from "../../../utils/DataTableLoader/CustomLoader";
import { Tooltip } from 'react-tooltip'
import UserSelector from "../../../Components/UserSelector/UserSelector";
import ActivityLogModal from "../../../Components/ActivityLogModal";
import CommandLogModal from "../../../Components/CommandLogModal";
import StatusChip from "../../../Components/StatusChip/StatusChip";
import CSVExportModal from "../../../Components/CSVExport/CSVExportModal";
import TagSelector from "../../../Components/TagSelector/TagSelector";
import TagList from "../../../Components/TagDisplay/TagList";
import ManageTagsModal from "../../../Components/ManageTagsModal/ManageTagsModal";
import TruncatedText, { TruncatedTextTooltip, DateCell, COLUMN_WIDTHS } from "../../../Components/TruncatedText/TruncatedText";
import DateRangeFilter from "../../../Components/DateRangeFilter/DateRangeFilter";
import fetchTagsByIds from "../../../utils/Tags/fetchTagsByIds";
import { min } from 'date-fns';
import { useAuth, Can } from "../../../authentication/AuthContext";
import {
  LISTINGS,
  useCustomizableColumns,
  CustomizeTableModal,
  TableCustomizerMenu,
} from "../../../Components/TableCustomizer";

// Mirrors NO_PRODUCT in the backend's utils/productFilters.js; the two must stay
// in step. Licenses with no product are valid for every product, deliberately,
// and `= NULL` can never match — the backend turns this sentinel into IS NULL.
const NO_PRODUCT = "__none__";

// Mirrors utils/licenseExpiryMail.js on the backend (RENEWAL_WINDOW_DAYS and
// licenseMailFor). Used ONLY to word the confirm dialogs and to predict the
// email count the admin confirms — the server re-derives everything from
// expires_at, so a stale row here can misword a prompt but never send the
// wrong email. Same 30 days as this listing's "Expiring Soon" chip.
const RENEWAL_WINDOW_DAYS = 30;

const previewLicenseMail = (expiresAt) => {
  const expDate = new Date(expiresAt);
  if (!expiresAt || isNaN(expDate.getTime()) || expDate.getTime() <= Date.now()) {
    return { template: "expired", days: 0 };
  }
  const days = Math.max(Math.ceil((expDate.getTime() - Date.now()) / 86400000), 1);
  return { template: days > RENEWAL_WINDOW_DAYS ? "not_due" : "expiring_soon", days };
};

// Mirrors planLicenseMails(): each person gets at most one email per state,
// each listing all of their selected licenses in that state — so someone with
// an expired AND an expiring license counts as two emails.
const previewBulkLicenseMails = (rows) => {
  const byEmail = new Map();
  let notDue = 0;
  for (const row of rows) {
    const email = (row.email || "").trim().toLowerCase();
    if (!email) continue;
    const { template } = previewLicenseMail(row.expires_at);
    if (template === "not_due") { notDue++; continue; }
    if (!byEmail.has(email)) byEmail.set(email, { expired: 0, expiring_soon: 0 });
    byEmail.get(email)[template]++;
  }
  let expired = 0;
  let expiring = 0;
  for (const counts of byEmail.values()) {
    if (counts.expired) expired++;
    if (counts.expiring_soon) expiring++;
  }
  return { people: byEmail.size, emails: expired + expiring, expired, expiring, notDue };
};

// Single source for the Product filter. getProductLabel reads the same list, so
// restoring a filter from the URL cannot disagree with the dropdown.
const PRODUCT_OPTIONS = [
  { value: "", label: "All" },
  { value: "SPCAD_ACAD_Beta", label: "AUTOCAD" },
  { value: "SPCAD_CADMATE", label: "CADMATE" },
  { value: "SPCAD_GSTARCAD", label: "GSTARCAD" },
  { value: "SPCAD_BRX", label: "BRICSCAD" },
  { value: "SPCAD_ZWCAD", label: "ZWCAD" },
  { value: "SPCAD_PCAD", label: "PCAD" },
  { value: NO_PRODUCT, label: "Universal (all products)" },
];

export default function AllLicenses() {
  const navigate = useNavigate();
  // Server-verified userType (NOT localStorage, which is editable).
  const { userType, can } = useAuth();
  const isReseller = userType === 'reseller';

  // Row selection exists only to drive the bulk actions, so the toggle is hidden
  // unless at least one of them is available.
  const canBulkAct = can(['admin.licenses.bulk_edit', 'admin.tags.bulk_assign', 'admin.licenses.send_bulk_expiry_email']);

  // The row actions and the mobile "⋮" menu that mirrors them. Checked as a group
  // so neither an empty toolbar nor a dropdown toggle that opens onto nothing is
  // rendered.
  const canRowAct = can([
    'admin.licenses.update',
    'admin.users.invoice',
    'admin.activity_logs.index',
    'admin.command_logs.index',
    'admin.licenses.send_expiry_email',
  ]);
  const canViewLogs = can(['admin.activity_logs.index', 'admin.command_logs.index']);

  // Same table, different rows: the admin endpoint is unscoped, the reseller one
  // returns only licenses belonging to that reseller's own customers. See the
  // note in Users/index.js — the split replaced an in-handler row filter, so the
  // scope now follows from which URL is called. Response shapes match, so only
  // the base path changes.
  const licensesBase = isReseller
    ? `${process.env.REACT_APP_API_URL}/reseller/licenses`
    : `${process.env.REACT_APP_API_URL}/admin/licenses`;
  const [screenloader, setScreenloader] = useState(true);
  const now = new Date();

  const [TableData, setTableData] = useState([]);
  const [TotalRecords, setTotalRecords] = useState();
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [pending, setPending] = useState(true);


  const [selectable, setSelectable] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showManageTagsModal, setShowManageTagsModal] = useState(false);
  const [licenseExpiry, setLicenseExpiry] = useState("");
  const [licenseUserId, setLicenseUserId] = useState(null);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedLicenseId, setSelectedLicenseId] = useState(null);
  const [showCommandModal, setShowCommandModal] = useState(false);
  const [selectedCommandLicenseId, setSelectedCommandLicenseId] = useState(null);

  const toggleSelectionMode = () => { setSelectedRows([]); setSelectable(!selectable); };
  const handleRowSelected = ({ selectedRows }) => { setSelectedRows(selectedRows); };
  const handleLicenseBulkUpdate = async () => {
    if ((!licenseExpiry && !licenseUserId) || selectedRows.length === 0) {
      alert("Please select rows and set an expiration date or license user.");
      return;
    }

    const selectedIds = selectedRows.map((row) => row.id);

    setScreenloader(true);

    await axios.put(`${process.env.REACT_APP_API_URL}/admin/licenses/bulk_edit`, {
      ids: selectedIds,
      expires_at: licenseExpiry,
      user_id: licenseUserId
    }, {
      headers: {
        'access-token': localStorage.getItem('accessToken'),
      },
    })
      .then(res => {
        setScreenloader(false);
        if (res.data.status) {
          toast.success("Licenses updated successfully!");
          getList();
          setShowModal(false);
          setLicenseExpiry("");
          setLicenseUserId(null);
          return;
        }
        else if (res.data.status === false) {
          toast.success('Could not update licnese!');
          return;
        } else {
          toast.error('Something Went Wrong');
          console.log(res.data);
          return;
        }
      })
      .catch(er => {
        setScreenloader(false);
        if (er.response.data?.message) {
          toast.error(er.response.data.message)
        } else {
          console.log(er)
          toast.error("Something Went Wrong")
        }
      })
  };

  //filters
  const initialFilters = {
    last_seen: { operator: '', value: '' },
    status: { operator: '', value: '' },
    'l.created_at': { operator: '', value: '' },
    'l.expires_at': { operator: '', value: '' },
    'u.email': { operator: '', value: '' },
    product: { operator: '', value: '' },
    'u.organization': { operator: '', value: '' },
    product_version: { operator: '', value: '' },
    'u.country': { operator: '', value: '' },
    license_reference_id: { operator: '', value: '' },
    'u.fname': { operator: '', value: '' },
    'u.lname': { operator: '', value: '' },
    'tg.tag_id': { operator: '', value: '' },
    // Drops every license carrying any of the selected tags. Handled by a
    // subquery on the backend, so it stacks with the include filter above.
    exclude_tag_id: { operator: '', value: '' },
    'ru.email': { operator: '', value: '' },
  };
  const [filters, setFilters] = useState(initialFilters);
  const [isInitialized, setIsInitialized] = useState(false);
  const [status, setStatus] = useState("");
  const [lastSeen, setLastSeen] = useState([null, null]);
  const [createdAt, setCreatedAt] = useState([null, null]);
  const [expiresAt, setExpiresAt] = useState([null, null]);
  const [product, setProduct] = useState("");
  const [email, setEmail] = useState("");
  const [organization, setOrganization] = useState("");
  const [version, setVersion] = useState("");
  const [country, setCountry] = useState([]);
  const [refId, setRefId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [tags, setTags] = useState([]);
  const [excludedTags, setExcludedTags] = useState([]);
  const [resellerEmail, setResellerEmail] = useState("");

  // Reseller renew modal
  const DAYS_PER_UNIT = { days: 1, months: 30, years: 365 };
  const DURATION_UNIT_OPTIONS = [
    { value: "days",   label: "Days" },
    { value: "months", label: "Months" },
    { value: "years",  label: "Years" },
  ];
  const [availableCredits, setAvailableCredits] = useState(0);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewingLicense, setRenewingLicense] = useState(null);
  const [renewValue, setRenewValue] = useState(1);
  const [renewUnit, setRenewUnit] = useState("years");
  const [renewing, setRenewing] = useState(false);
  const [renewNotify, setRenewNotify] = useState(true);
  const renewCredits = (parseInt(renewValue, 10) || 0) * (DAYS_PER_UNIT[renewUnit] || 0);

  const normalizedCountryList = () => {
    const list = countryList().getData();
    return list.map((country) =>
      country.label === "Viet Nam" ? { ...country, label: "Vietnam" } : country
    );
  };

  const CountriesList = useMemo(() => normalizedCountryList(), []);

  const addDaysToDate = (dateStr, days) => {
    const d = dateStr && new Date(dateStr) > new Date() ? new Date(dateStr) : new Date();
    d.setDate(d.getDate() + days);
    return d;
  };

  useEffect(() => {
    if (!isReseller) return;
    axios.get(`${process.env.REACT_APP_API_URL}/reseller/dashboard`, {
      headers: { 'access-token': localStorage.getItem('accessToken') },
    }).then(res => {
      if (res.data.status) setAvailableCredits(res.data.profile.available_credits);
    }).catch(() => { });
  }, []);

  const handleRenew = async () => {
    if (!renewingLicense) return;
    setRenewing(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/reseller/licenses/${renewingLicense.id}/renew`,
        { duration_value: renewValue, duration_unit: renewUnit, notify_customer: renewNotify },
        { headers: { 'access-token': localStorage.getItem('accessToken') } }
      );
      if (res.data.status) {
        if (res.data.email_error) {
          toast.error(res.data.message);
        } else {
          toast.success(res.data.message || 'License renewed successfully!');
        }
        setAvailableCredits(prev => prev - renewCredits);
        setShowRenewModal(false);
        setRenewingLicense(null);
        setRenewValue(1);
        setRenewUnit("years");
        getList();
      } else {
        toast.error(res.data.message || 'Failed to renew license');
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      setRenewing(false);
    }
  };

  const handleFiltersChange = (key, value, operator) => {
    const newFilters = {
      ...filters,
      [key]: {
        operator: operator,
        value: value
      }
    };
    setFilters(newFilters);
    updateURLParams(newFilters);
  };

  const updateURLParams = (currentFilters) => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('pageSize', pageSize);

    // Add sorting params
    if (sortBy) params.set('sortBy', sortBy);
    if (sortOrder) params.set('sortOrder', sortOrder);

    // Add filter params
    Object.entries(currentFilters).forEach(([key, filter]) => {
      if (filter.value) {
        params.set(`${key}_operator`, filter.operator);
        params.set(`${key}_value`, filter.value);
      }
    });

    navigate(`${window.location.pathname}?${params.toString()}`, { replace: true });
  };

  const handleFiltersReset = () => {
    setFilters(initialFilters);
    setStatus("");
    setLastSeen([null, null]);
    setCreatedAt([null, null]);
    setExpiresAt([null, null]);
    setProduct("");
    setEmail("");
    setOrganization("");
    setCountry([]);
    setVersion("");
    setRefId("");
    setFirstName("");
    setLastName("");
    setTags([]);
    setExcludedTags([]);
    setResellerEmail("");
    updateURLParams(initialFilters);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Restore pagination
    const urlPage = params.get('page');
    const urlPageSize = params.get('pageSize');
    if (urlPage) setPage(Number(urlPage));
    if (urlPageSize) setPageSize(Number(urlPageSize));

    // Restore sorting
    const urlSortBy = params.get('sortBy');
    const urlSortOrder = params.get('sortOrder');
    if (urlSortBy) setSortBy(urlSortBy);
    if (urlSortOrder) setSortOrder(urlSortOrder);

    // Restore filters
    const restoredFilters = {};
    Object.keys(initialFilters).forEach(key => {
      const operator = params.get(`${key}_operator`);
      const value = params.get(`${key}_value`);
      var formattedValue;
      if (operator && value) {
        if (['last_seen'].includes(key)) {
          formattedValue = new Date(value).toISOString()
        } else if (['l.created_at', 'l.expires_at'].includes(key)) {
          if (typeof value === 'string' && value.includes(',')) {
            var values = value.split(",");
            formattedValue = [new Date(values[0]), new Date(values[1])];
          } else {
            formattedValue = [new Date(value), null];
          }
        } else {
          formattedValue = value;
        }

        restoredFilters[key] = { operator, value: formattedValue };
        // Restore individual filter states
        switch (key) {
          case 'status':
            setStatus({ value: value, label: value === '1' ? 'Active' : value === '0' ? 'Expired' : 'All' });
            break;
          case 'last_seen':
            setLastSeen(formattedValue);
            break;
          case 'l.created_at':
            setCreatedAt(formattedValue);
            break;
          case 'l.expires_at':
            setExpiresAt(formattedValue);
            break;
          case 'product':
            setProduct({ value: value, label: getProductLabel(value) });
            break;
          case 'u.email':
            setEmail(value.replace(/%/g, ''));
            break;
          case 'u.organization':
            setOrganization(value.replace(/%/g, ''));
            break;
          case 'license_reference_id':
            setRefId(value.replace(/%/g, ''));
            break;
          case 'u.fname':
            setFirstName(value.replace(/%/g, ''));
            break;
          case 'u.lname':
            setLastName(value.replace(/%/g, ''));
            break;
          case 'u.country':
            // eslint-disable-next-line
            const countryOption = CountriesList.find(c => c.label === value);
            if (countryOption) setCountry(countryOption);
            break;
          case 'product_version':
            setVersion(value.replace(/%/g, ''));
            break;
          case 'tg.tag_id':
            // The URL only carries ids — resolve them so the chips come back.
            fetchTagsByIds(value).then(setTags);
            break;
          case 'exclude_tag_id':
            fetchTagsByIds(value).then(setExcludedTags);
            break;
          case 'ru.email':
            setResellerEmail(value.replace(/%/g, ''));
            break;
          default:
            break;
        }

      }
    });

    if (Object.keys(restoredFilters).length > 0) {
      setFilters(restoredFilters);
    }
    setIsInitialized(true);

  }, []);

  // Label for a product value restored from the URL.
  const getProductLabel = (value) => {
    const option = PRODUCT_OPTIONS.find(opt => opt.value === value);
    return option ? option.label : "All";
  };

  const handleEnterKeyPress = (e) => {
    var value = e.target.value;
    if (value !== "") {
      value = `%${value}%`
    }
    if (e.key === 'Enter') {
      handleFiltersChange(e.target.id, value, 'LIKE')
    }
  };

  const handleCopyEmailList = () => {
    const emails = Array.from(new Set(selectedRows.map(license => license.email)));
    const emailList = emails.join(', ');
    try {
      navigator.clipboard.writeText(emailList);
      toast.success(`${emails.length} Unique emails copied from selected to clipboard!`);
    } catch (error) {
      toast.error('Failed to copy email list to clipboard');
    }
  };


  const sendRenewalEmail = async (row) => {
    setScreenloader(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/licenses/${row.id}/send_expiry_email`,
        {},
        { headers: { "access-token": localStorage.getItem("accessToken") } }
      );
      if (response.data.status) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message || "Could not send the email.");
      }
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        console.error(error);
        toast.error("Something Went Wrong");
      }
    } finally {
      setScreenloader(false);
    }
  };

  const handleRenewalEmailClick = (row) => {
    if (!row.email) {
      toast.error("This license's owner has no email address on file.");
      return;
    }
    const { template, days } = previewLicenseMail(row.expires_at);
    if (template === "not_due") {
      toast.error(`This license has ${days} days left. Renewal emails only go out within ${RENEWAL_WINDOW_DAYS} days of expiry.`);
      return;
    }
    const what = template === "expired"
      ? 'the "license has expired" email'
      : `the "expires in ${days} day${days === 1 ? "" : "s"}" reminder`;
    confirmAlert({
      title: "Send renewal email?",
      message: `This sends ${what} for license ${row.license_reference_id} to ${row.email}. The customer receives it immediately.`,
      buttons: [
        { label: "Send", onClick: () => sendRenewalEmail(row) },
        { label: "Cancel" },
      ],
    });
  };

  const sendBulkRenewalEmails = async (payload) => {
    setScreenloader(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/licenses/send_expiry_emails`,
        payload,
        { headers: { "access-token": localStorage.getItem("accessToken") } }
      );
      if (response.data.status) {
        // The backend reports what it actually sent, which can be less than
        // planned if Mailgun rejected some addresses.
        if (response.data.failed) {
          toast.error(response.data.message);
        } else {
          toast.success(response.data.message);
        }
      } else {
        toast.error(response.data.message || "Could not send the emails.");
      }
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        console.error(error);
        toast.error("Something Went Wrong");
      }
    } finally {
      setScreenloader(false);
    }
  };

  // Selection only, as on the trial listing: mailing customers is an explicit
  // act of picking rows, never a side effect of the applied filters.
  const handleBulkRenewalEmailClick = () => {
    const plan = previewBulkLicenseMails(selectedRows);
    if (!plan.emails) {
      toast.error(plan.notDue
        ? `None of the selected licenses are within ${RENEWAL_WINDOW_DAYS} days of expiry.`
        : "None of the selected licenses have an email address.");
      return;
    }
    const parts = [];
    if (plan.expired) parts.push(`${plan.expired} "expired" ${plan.expired === 1 ? "notice" : "notices"}`);
    if (plan.expiring) parts.push(`${plan.expiring} "expiring soon" ${plan.expiring === 1 ? "reminder" : "reminders"}`);
    // Named rather than just counted: a selected license silently missing from
    // the email is exactly what this dialog must make obvious in advance.
    const notDueRefs = selectedRows
      .filter(r => r.email && previewLicenseMail(r.expires_at).template === "not_due")
      .map(r => r.license_reference_id);
    const skipped = notDueRefs.length
      ? ` Left out — more than ${RENEWAL_WINDOW_DAYS} days from expiry: ${notDueRefs.slice(0, 5).join(", ")}${notDueRefs.length > 5 ? ` and ${notDueRefs.length - 5} more` : ""}.`
      : "";
    confirmAlert({
      title: "Send renewal emails?",
      message:
        `This sends ${plan.emails} ${plan.emails === 1 ? "email" : "emails"} to ${plan.people} ` +
        `${plan.people === 1 ? "person" : "people"}: ${parts.join(" and ")}. ` +
        `Each email lists all of that person's selected licenses in that state.${skipped} This cannot be undone.`,
      buttons: [
        {
          label: `Send ${plan.emails}`,
          onClick: () => sendBulkRenewalEmails({
            ids: selectedRows.map(r => r.id),
            confirmed_count: plan.emails,
          }),
        },
        { label: "Cancel" },
      ],
    });
  };

  const handleActivityClick = (licenseId) => {
    setSelectedLicenseId(licenseId);
    setShowActivityModal(true);
  };

  const handleCommandLogClick = (licenseId) => {
    setSelectedCommandLicenseId(licenseId);
    setShowCommandModal(true);
  };

  const renewalEmailTooltip = (row) => {
    const { template } = previewLicenseMail(row.expires_at);
    if (template === "expired") return "Send expiry email";
    if (template === "expiring_soon") return "Send renewal reminder";
    return `Renewal emails open ${RENEWAL_WINDOW_DAYS} days before expiry`;
  };

  // Every column this listing can show. `columns` below is the subset the current
  // user has chosen, in their order — that is what the table renders, while the
  // CSV exporter keeps using this one so hiding a column doesn't remove it from an
  // export.
  const allColumns = [
    {
      name: "Actions",
      // Hidden entirely rather than left as an empty column: omit drops the
      // header as well as the cells.
      omit: !((isReseller ? can(['reseller.users.show', 'reseller.licenses.renew']) : canRowAct)),
      sortable: false,
      minWidth: isReseller ? '90px' : '130px',
      cell: row => (
        <div className="action-buttons-container">
          {!isReseller ? (
            <>
              {/* Three primary actions stay inline; the two log viewers live in
                  a "⋮" menu. With all five inline the row outgrew the column
                  and the last icon slid under the next cell, where it could be
                  seen but not clicked. */}
              <div className="desktop-actions d-none d-xl-flex">
                <Can permission="admin.licenses.update">
                  <FaEdit
                    data-tooltip-id="license-index-edit"
                    data-tooltip-content="Edit license"
                    data-tooltip-place="top"
                    className="edit-icon"
                    onClick={() => navigate(`/Admin/Licenses/${row.id}/edit`)}
                  />
                </Can>
                <Can permission="admin.users.invoice">
                  <FaRegIdCard
                    data-tooltip-id="license-index-view-invoice"
                    data-tooltip-content="View Invoice"
                    data-tooltip-place="top"
                    className="edit-icon"
                    onClick={() => navigate(`/Admin/Licenses/Invoice/${row.user_id}`)}
                  />
                </Can>
                <Can permission="admin.licenses.send_expiry_email">
                  <FaEnvelope
                    data-tooltip-id="license-index-renewal-email"
                    data-tooltip-content={renewalEmailTooltip(row)}
                    data-tooltip-place="top"
                    className="edit-icon"
                    style={previewLicenseMail(row.expires_at).template === "not_due" ? { opacity: 0.35 } : undefined}
                    onClick={() => handleRenewalEmailClick(row)}
                  />
                </Can>
                {canViewLogs && (
                  <Dropdown drop="end">
                    <Dropdown.Toggle
                      as="div"
                      id={`dropdown-logs-${row.id}`}
                      className="p-0 border-0 no-caret-toggle"
                      data-tooltip-id="license-index-logs"
                      data-tooltip-content="Logs"
                      data-tooltip-place="top"
                    >
                      <FaEllipsisV className="edit-icon" style={{ margin: 0 }} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Can permission="admin.activity_logs.index">
                        <Dropdown.Item onClick={() => handleActivityClick(row.id)}>
                          <FaRegClock className="me-2 text-warning" /> Activity Log
                        </Dropdown.Item>
                      </Can>
                      <Can permission="admin.command_logs.index">
                        <Dropdown.Item onClick={() => handleCommandLogClick(row.id)}>
                          <FaTerminal className="me-2 text-warning" /> Command Logs
                        </Dropdown.Item>
                      </Can>
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>

              {canRowAct && (
              <div className="mobile-actions d-xl-none">
                <Dropdown drop="start">
                  <Dropdown.Toggle as="div" id={`dropdown-actions-${row.id}`} className="p-0 border-0 no-caret-toggle">
                    <FaEllipsisV className="edit-icon" style={{ margin: 0 }} />
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Can permission="admin.licenses.update">
                      <Dropdown.Item onClick={() => navigate(`/Admin/Licenses/${row.id}/edit`)}>
                        <FaEdit className="me-2 text-warning" /> Edit License
                      </Dropdown.Item>
                    </Can>
                    <Can permission="admin.users.invoice">
                      <Dropdown.Item onClick={() => navigate(`/Admin/Licenses/Invoice/${row.user_id}`)}>
                        <FaRegIdCard className="me-2 text-warning" /> View Invoice
                      </Dropdown.Item>
                    </Can>
                    <Can permission="admin.activity_logs.index">
                      <Dropdown.Item onClick={() => handleActivityClick(row.id)}>
                        <FaRegClock className="me-2 text-warning" /> Activity Log
                      </Dropdown.Item>
                    </Can>
                    <Can permission="admin.command_logs.index">
                      <Dropdown.Item onClick={() => handleCommandLogClick(row.id)}>
                        <FaTerminal className="me-2 text-warning" /> Command Logs
                      </Dropdown.Item>
                    </Can>
                    <Can permission="admin.licenses.send_expiry_email">
                      <Dropdown.Item onClick={() => handleRenewalEmailClick(row)}>
                        <FaEnvelope className="me-2 text-warning" /> Renewal Email
                      </Dropdown.Item>
                    </Can>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              )}
            </>
          ) : (
            <>
              <Can permission="reseller.users.show">
                <FaEye
                  data-tooltip-id="license-index-view-user"
                  data-tooltip-content="View user"
                  data-tooltip-place="top"
                  className="edit-icon"
                  onClick={() => navigate(`/Admin/Users/${row.user_id}`)}
                />
              </Can>
              <Can permission="reseller.licenses.renew">
                <FaRedo
                  data-tooltip-id="license-index-renew"
                  data-tooltip-content="Renew license"
                  data-tooltip-place="top"
                  className="edit-icon"
                  onClick={() => { setRenewingLicense(row); setRenewValue(1); setRenewUnit("years"); setRenewNotify(true); setShowRenewModal(true); }}
                />
              </Can>
            </>
          )}
        </div>
      )
    },
    {
      name: 'Status',
      minWidth: '150px',
      selector: row => (row.status),
      sortField: "status",
      sortable: true,
      cell: row => (<StatusChip expiresAt={row.expires_at} statusFlag={row.status} expiringSoondays={30} pill={true} />),
      exportCell: (row) => { const statusFlag = row.status; const expiresAt = row.expires_at; if (!expiresAt) return 'Active'; const expDate = new Date(expiresAt); const nowLocal = new Date(); const msDiff = expDate.getTime() - nowLocal.getTime(); const daysDiff = msDiff / (1000 * 60 * 60 * 24); if (daysDiff < 0) { return statusFlag === 'active' ? 'Expired (Pending)' : 'Expired'; } if (daysDiff <= 30) return 'Expiring Soon'; return 'Active'; },
    },
    {
      name: 'Ref Id',
      minWidth: `${COLUMN_WIDTHS.refId}px`,
      selector: row => (row.license_reference_id),
      sortField: "license_reference_id",
      sortable: true,
      cell: row => <TruncatedText text={row.license_reference_id} maxWidth={COLUMN_WIDTHS.refId} mono />,
      exportCell: (row) => row.license_reference_id,
    },
    {
      name: 'Email',
      minWidth: `${COLUMN_WIDTHS.email}px`,
      selector: row => row.email,
      sortField: "email",
      sortable: true,
      cell: row => <TruncatedText text={row.email} maxWidth={COLUMN_WIDTHS.email} />,
      exportCell: (row) => row.email,
    },
    ...(!isReseller ? [{
      name: 'Reseller',
      minWidth: `${COLUMN_WIDTHS.email}px`,
      selector: row => row.reseller_email,
      sortable: false,
      cell: row => row.reseller_email
        ? <TruncatedText text={row.reseller_email} maxWidth={COLUMN_WIDTHS.email} />
        : <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>,
      exportCell: (row) => row.reseller_email || '—',
    }] : []),
    {
      name: 'Last Update',
      minWidth: `${COLUMN_WIDTHS.date}px`,
      selector: row => row.updated_at,
      sortField: "updated_at",
      sortable: true,
      cell: row => <DateCell value={ShowTime(row.updated_at)} />,
      exportCell: (row) => ShowTime(row.updated_at),
    },
    ...(!isReseller ? [{
      name: 'Tags',
      selector: row => row.tags,
      sortable: false,
      cell: row => <TagList tags={row.tags} />,
    }] : []),
    {
      name: 'Start Date',
      minWidth: `${COLUMN_WIDTHS.date}px`,
      selector: row => row.created_at,
      sortField: "created_at",
      sortable: true,
      cell: row => <DateCell value={ShowTime(row.created_at)} />,
      exportCell: (row) => ShowTime(row.created_at),
    },
    {
      name: 'End Date',
      minWidth: `${COLUMN_WIDTHS.date}px`,
      selector: row => row.expires_at,
      sortField: "expires_at",
      sortable: true,
      cell: row => <DateCell value={ShowTime(row.expires_at)} />,
      exportCell: (row) => ShowTime(row.expires_at),
    },
    {
      name: 'Product',
      selector: row => row.product,
      sortField: "product",
      sortable: true,
      minWidth: `${COLUMN_WIDTHS.product}px`,
      cell: row => (
        <TruncatedText text={row.product ? MapProduct(row.product, "simple") : "Universal"} maxWidth={COLUMN_WIDTHS.product}>
          {row.product ? MapProduct(row.product) : "Universal"}
        </TruncatedText>
      ),
      exportCell: (row) => row.product || "Universal",
    },
    {
      name: 'Machines Assigned',
      minWidth: '150px',
      selector: row => row.assigned_machine_count,
      sortField: "assigned_machine_count",
      sortable: true,
      cell: row => <div>{row.assigned_machine_count} {row.assigned_machine_count > 0 && (
        <Can permission="admin.licenses.assigned_machines">
          <FaLaptopMedical
            data-tooltip-id="license-index-attached-machines"
            data-tooltip-content="view attached machines"
            data-tooltip-place="top"
            className="edit-icon"
            onClick={() => navigate(`/Admin/Licenses/${row.id}/AssignedMachines`)}
          />
        </Can>
      )}</div>,
      exportCell: (row) => row.assigned_machine_count,
    },
    {
      name: 'Machine User',
      minWidth: `${COLUMN_WIDTHS.machine}px`,
      selector: row => row.machine_user,
      sortField: "machine_user",
      sortable: true,
      cell: row => <TruncatedText text={row.machine_user} maxWidth={COLUMN_WIDTHS.machine} />,
      exportCell: (row) => row.machine_user,

    },
    {
      name: 'Version',
      selector: row => row.product_version,
      sortField: "product_version",
      sortable: true,
      cell: row => <div>{row.product_version ? row.product_version : '-'}</div>,
      exportCell: (row) => (row.product_version ? row.product_version : '-'),
    },
    {
      name: 'Last Action',
      minWidth: `${COLUMN_WIDTHS.notes}px`,
      selector: row => row.last_action,
      sortField: "last_action",
      sortable: true,
      cell: row => <TruncatedText text={row.last_action} maxWidth={COLUMN_WIDTHS.notes} />,
      exportCell: (row) => row.last_action,
    },
    {
      name: 'Locked',
      selector: row => row.locked,
      sortField: "locked",
      sortable: true,
      cell: row => <div style={{ whiteSpace: 'pre-wrap' }}>{row.locked ? "In use" : "Free"}</div>,
      exportCell: (row) => (row.locked ? 'In use' : 'Free'),
    },
    {
      name: 'First Name',
      minWidth: `${COLUMN_WIDTHS.name}px`,
      selector: row => row.fname,
      sortField: "fname",
      sortable: true,
      cell: row => <TruncatedText text={row.fname} maxWidth={COLUMN_WIDTHS.name} />,
      exportCell: (row) => (row.fname ? row.fname : '-'),
    },
    {
      name: 'Last Name',
      minWidth: `${COLUMN_WIDTHS.name}px`,
      selector: row => row.lname,
      sortField: "lname",
      sortable: true,
      cell: row => <TruncatedText text={row.lname} maxWidth={COLUMN_WIDTHS.name} />,
      exportCell: (row) => (row.lname ? row.lname : '-'),
    },
    {
      name: 'Country',
      minWidth: `${COLUMN_WIDTHS.country}px`,
      selector: row => row.country,
      sortField: "country",
      sortable: true,
      cell: row => <TruncatedText text={row.country} maxWidth={COLUMN_WIDTHS.country} />,
      exportCell: (row) => (row.country ? row.country : '-'),
    },
    {
      name: 'Organization',
      minWidth: `${COLUMN_WIDTHS.organization}px`,
      selector: row => row.organization,
      sortField: "organization",
      sortable: true,
      cell: row => <TruncatedText text={row.organization} maxWidth={COLUMN_WIDTHS.organization} />,
      exportCell: (row) => (row.organization ? row.organization : '-'),
    },

  ];

  // Resellers share this screen and store under the same listing key. That is
  // fine: a user has exactly one portal, and any column their role can't see is
  // already dropped by `omit` before the picker ever sees it — so a reseller's
  // saved layout simply contains fewer ids than an admin's.
  //
  // "Actions" is locked: it is the only route to the edit screen, the invoice and
  // the log modals, so it stays visible and leftmost.
  const {
    columns,
    ready: columnsReady,
    menuProps: tableMenuProps,
    customizerProps: tableCustomizerProps,
  } = useCustomizableColumns({
    listingKey: LISTINGS.ADMIN_LICENSES,
    columns: allColumns,
    lockedColumnIds: ["Actions"],
  });

  const getList = async () => {
    try {
      const formattedFilters = Object.keys(filters)
        .filter(key => filters[key].value) // Filter out falsy values
        .map((key, index) => ({
          [`filters[${index}][column]`]: key,
          [`filters[${index}][operator]`]: filters[key].operator,
          [`filters[${index}][value]`]: filters[key].value,
        }))
        .reduce((acc, obj) => ({ ...acc, ...obj }), {});

      const response = await axios.get(licensesBase, {
        params: {
          limit: pageSize,
          page: page,
          sort_by: sortBy,
          sort_order: sortOrder,
          ...formattedFilters
        },
        headers: {
          'access-token': localStorage.getItem('accessToken'),
        },
      });
      setScreenloader(false);
      setPending(false);
      if (response.data.status) {
        const total = response.data.meta.pagination.total;
        setTotalRecords(total);
        setTableData(response.data.licenses);
      } else {
        toast.error('Something Went Wrong');
        console.log(response.data);
      }
    } catch (error) {
      setScreenloader(false);
      setPending(false);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        console.error(error);
        toast.error('Something Went Wrong');
      }
    }
  };

  useEffect(() => {
    if (!isInitialized) return;
    setPending(true);
    getList();
  }, [filters, page, pageSize, sortBy, sortOrder, isInitialized]);

  const conditionalRowStyles = [
    {
      //Active licenses
      when: row => (new Date(row.expires_at) > now),
      style: {
        backgroundColor: '#8ebca1ff',
        color: 'black',
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
    {
      //Expired licenses
      when: row => (new Date(row.expires_at) < now && row.status === false),
      style: {
        backgroundColor: '#f3f2f2ff',
        color: 'black',
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
    {
      //Expired but user hasn't logged for the status to be updated
      when: row => (new Date(row.expires_at) < now && row.status === true),
      style: {
        backgroundColor: '#f3f2f2ff',
        color: 'black',
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
    {
      //Expiring soon
      when: row => (isExpiringSoon(row.expires_at)),
      style: {
        backgroundColor: '#8478afcb',
        color: 'black',
        '&:hover': {
          cursor: 'pointer',
        },
      },
    }
  ]
  const isExpiringSoon = (expiresAt) => {
    const expDate = new Date(expiresAt);
    if (expDate <= now) return false;
    const msInDay = 24 * 60 * 60 * 1000;
    const threshold = now.getTime() + 30 * msInDay;
    return expDate.getTime() > now.getTime() && expDate.getTime() <= threshold;
  };


  return (
    <div>
      <AdminDashboardLayout />
      <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
      <div className='content-page-bg'>
        <div className='content-page'>
          <div className='content'>

            <div className="row align-items-center mb-3">
              <div className="col-12 col-md-10 mb-2 mb-md-0">
                <h2 className="m-0">Licenses</h2>
              </div>

              <Can permission="admin.licenses.create">
                <div className="col-12 col-md-2 d-flex justify-content-md-end">
                  <Button
                    className="admin-btn w-100 w-md-auto"
                    onClick={() => navigate('/Admin/Licenses/Add')}
                  >
                    + New License
                  </Button>
                </div>
              </Can>
            </div>


            <div className="filters row">
              <div className="col-md-3 mb-3">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    id="license_reference_id"
                    value={refId}
                    placeholder="Reference ID"
                    onChange={(e) => setRefId(e.target.value)}
                    onKeyDown={handleEnterKeyPress}
                  />
                  {refId && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => handleEnterKeyPress({ key: 'Enter', target: { id: 'license_reference_id', value: refId } })}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-3 mb-3">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    id="u.fname"
                    value={firstName}
                    placeholder="First Name"
                    onChange={(e) => setFirstName(e.target.value)}
                    onKeyDown={handleEnterKeyPress}
                  />
                  {firstName && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => handleEnterKeyPress({ key: 'Enter', target: { id: 'u.fname', value: firstName } })}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-3 mb-3">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    id="u.lname"
                    value={lastName}
                    placeholder="Last Name"
                    onChange={(e) => setLastName(e.target.value)}
                    onKeyDown={handleEnterKeyPress}
                  />
                  {lastName && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => handleEnterKeyPress({ key: 'Enter', target: { id: 'u.lname', value: lastName } })}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-3 mb-3">
                <div className="input-group">
                  <input
                    type="email"
                    className="form-control"
                    id="u.email"
                    value={email}
                    placeholder="Email"
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={handleEnterKeyPress}
                  />
                  {email && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => handleEnterKeyPress({ key: 'Enter', target: { id: 'u.email', value: email } })}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-3 mb-3">
                <Select
                  classNamePrefix="rs"
                  placeholder="Country"
                  options={CountriesList}
                  value={country}
                  onChange={(e) => { setCountry(e); handleFiltersChange('u.country', e.label, '=') }}
                />
              </div>

              <div className="col-md-3 mb-3">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    id="u.organization"
                    value={organization}
                    placeholder="Organization"
                    onChange={(e) => setOrganization(e.target.value)}
                    onKeyDown={handleEnterKeyPress}
                  />
                  {organization && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => handleEnterKeyPress({ key: 'Enter', target: { id: 'u.organization', value: organization } })}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>

              {!isReseller && (
                <div className="col-md-3 mb-3">
                  <div className="input-group">
                    <input
                      type="email"
                      className="form-control"
                      id="ru.email"
                      value={resellerEmail}
                      placeholder="Reseller Email"
                      onChange={(e) => setResellerEmail(e.target.value)}
                      onKeyDown={handleEnterKeyPress}
                    />
                    {resellerEmail && (
                      <span
                        className="input-group-text"
                        role="button"
                        onClick={() => handleEnterKeyPress({ key: 'Enter', target: { id: 'ru.email', value: resellerEmail } })}
                      >
                        Apply
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="col-md-3 mb-3">
                <Select
                  classNamePrefix="rs"
                  placeholder="Product"
                  options={PRODUCT_OPTIONS}
                  value={product}
                  onChange={(e) => { setProduct(e); handleFiltersChange('product', e.value, '=') }}
                />
              </div>

              <div className="col-md-3 mb-3">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    id="product_version"
                    value={version}
                    placeholder="Product Version"
                    onChange={(e) => setVersion(e.target.value)}
                    onKeyDown={handleEnterKeyPress}
                  />
                  {version && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => handleEnterKeyPress({ key: 'Enter', target: { id: 'product_version', value: version } })}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-3 mb-3">
                <Select
                  classNamePrefix="rs"
                  placeholder="Status"
                  options={[
                    { value: "", label: "All" },
                    { value: "1", label: "Active" },
                    { value: "0", label: "Expired" },
                  ]}
                  value={status}
                  onChange={(e) => { setStatus(e); handleFiltersChange('status', e.value, '=') }}
                />
              </div>

              <div className="col-md-3 mb-3">
                <DateRangeFilter
                  placeholderText="Last Seen"
                  startDate={lastSeen[0]}
                  endDate={lastSeen[1]}
                  onChange={(dates) => {
                    setLastSeen(dates);
                    const [start, end] = dates;
                    if (start && end) {
                      handleFiltersChange('last_seen', [start.toISOString(), end.toISOString()], 'BETWEEN');
                    } else if (!start && !end) {
                      handleFiltersChange('last_seen', '', '');
                    }
                  }}
                />
              </div>

              <div className="col-md-3 mb-3">
                <DateRangeFilter
                  placeholderText="Start Date"
                  startDate={createdAt[0]}
                  endDate={createdAt[1]}
                  onChange={(dates) => {
                    setCreatedAt(dates);
                    const [start, end] = dates;
                    if (start && end) {
                      handleFiltersChange('l.created_at', [start.toISOString(), end.toISOString()], 'BETWEEN');
                    } else if (!start && !end) {
                      handleFiltersChange('l.created_at', '', '');
                    }
                  }}
                />
              </div>

              <div className="col-md-3 mb-3">
                <DateRangeFilter
                  placeholderText="End Date"
                  startDate={expiresAt[0]}
                  endDate={expiresAt[1]}
                  onChange={(dates) => {
                    setExpiresAt(dates);
                    const [start, end] = dates;
                    if (start && end) {
                      handleFiltersChange('l.expires_at', [start.toISOString(), end.toISOString()], 'BETWEEN');
                    } else if (!start && !end) {
                      handleFiltersChange('l.expires_at', '', '');
                    }
                  }}
                />
              </div>

              {!isReseller && (
                <>
                  <div className="col-md-3 mb-3">
                    <TagSelector
                      placeholder="Tags"
                      value={tags}
                      onChange={(selected) => {
                        setTags(selected);
                        const values = selected ? selected.map(s => s.value).join(',') : '';
                        handleFiltersChange('tg.tag_id', values, 'IN');
                      }}
                    />
                  </div>

                  <div className="col-md-3 mb-3">
                    <TagSelector
                      placeholder="Exclude Tags"
                      value={excludedTags}
                      onChange={(selected) => {
                        setExcludedTags(selected);
                        const values = selected ? selected.map(s => s.value).join(',') : '';
                        handleFiltersChange('exclude_tag_id', values, 'NOT IN');
                      }}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="row g-2 align-items-end">
              {/* Left Section */}
              <div className="col-12 col-md-10 d-flex flex-wrap gap-2 align-items-center">
                {isReseller ? (
                  <Button className="admin-btn" onClick={() => setShowCSVModal(true)}>Export CSV</Button>
                ) : selectable ? (
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Button onClick={toggleSelectionMode} variant="secondary">
                      Cancel Selection
                    </Button>

                    {selectedRows.length > 0 && (
                      <>
                        <Can permission="admin.licenses.bulk_edit">
                          <Button className="admin-btn" onClick={() => setShowModal(true)}>
                            Update Licenses
                          </Button>
                        </Can>
                        <Can permission="admin.tags.bulk_assign">
                          <Button className="admin-btn" onClick={() => setShowManageTagsModal(true)}>
                            Manage Tags
                          </Button>
                        </Can>
                        {/* Export and Copy work off rows already loaded in the
                            browser — no further API call, so no permission to check. */}
                        <Button className="admin-btn" onClick={() => setShowCSVModal(true)}>
                          Export CSV
                        </Button>
                        <Button className="admin-btn" onClick={handleCopyEmailList}>
                          Copy Emails
                        </Button>
                        <Can permission="admin.licenses.send_bulk_expiry_email">
                          <Button variant="warning" onClick={handleBulkRenewalEmailClick}>
                            Send Renewal Emails
                          </Button>
                        </Can>
                      </>
                    )}

                    {selectedRows.length > 0 && (
                      <span className="ms-2 text-muted">
                        {selectedRows.length} Selected
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {canBulkAct && (
                      <Button className="admin-btn" onClick={toggleSelectionMode}>
                        Enable Selection
                      </Button>
                    )}
                    <Button className="admin-btn" onClick={() => setShowCSVModal(true)}>
                      Export CSV
                    </Button>
                  </div>
                )}
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-2 d-flex justify-content-md-end align-items-center gap-2">
                {JSON.stringify(filters) !== JSON.stringify(initialFilters) && (
                  <Button
                    onClick={handleFiltersReset}
                    variant="primary"
                    // Was w-100 on mobile; now it shares the row with the three-dot
                    // menu, so it grows to fill what's left instead of claiming the
                    // full width and pushing the menu out.
                    className="admin-btn flex-grow-1 flex-md-grow-0"
                  >
                    Clear all Filters
                  </Button>
                )}
                <TableCustomizerMenu {...tableMenuProps} />
              </div>

              <div className="row d-flex d-sm-none">
                <p>Total Records: {TotalRecords}</p>
              </div>
            </div>

            <CustomizeTableModal {...tableCustomizerProps} title="Customize Licenses Table" />

            <ManageTagsModal
              show={showManageTagsModal}
              onHide={() => setShowManageTagsModal(false)}
              selectedIds={selectedRows.map(row => row.id)}
              type="License"
              onSuccess={() => {
                getList();
                setSelectedRows([]);
                setSelectable(false); // Optionally exit selection mode
              }}
            />

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title>License bulk update</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <label htmlFor="product" className="form-label">
                  Change expiry date
                </label>
                <Form.Control
                  type='date'
                  required
                  value={licenseExpiry}
                  onChange={(event) => {
                    setLicenseExpiry(event.target.value)
                  }}
                />
                <label htmlFor="product" className="form-label mt-3">
                  Change license user
                </label>
                <UserSelector
                  type="user"
                  onSelectUser={(userId) => setLicenseUserId(userId)}
                />
              </Modal.Body>
              <Modal.Footer>
                <Button variant="primary" onClick={handleLicenseBulkUpdate}>
                  Update
                </Button>
              </Modal.Footer>
            </Modal>

            <CSVExportModal
              show={showCSVModal}
              onHide={() => setShowCSVModal(false)}
              columns={allColumns}
              rows={selectedRows.length > 0 ? selectedRows : TableData}
              filename={selectedRows.length > 0 ? `licenses_selected_${selectedRows.length}_rows.csv` : `licenses_visible_page_${page}_per_${pageSize}.csv`}
              excludeColumnNames={["Actions"]}
              preselectedColumnNames={["Ref Id", "Email", "Start Date", "End Date", "Product"]}
            />
            {/* Held until the saved column layout is known — rendering the
                defaults first and then re-laying-out is a visible jump. */}
            <div className="table-wrapper">
              {!columnsReady ? <CustomLoader /> : (
              <DataTable
                //data
                columns={columns}
                data={TableData}
                //style
                dense
                direction="auto"
                highlightOnHover
                noHeader
                persistTableHead
                pointerOnHover
                responsive
                subHeaderAlign="right"
                subHeaderWrap
                progressPending={pending}
                progressComponent={<CustomLoader />}
                conditionalRowStyles={conditionalRowStyles}
                //pagination
                pagination
                paginationServer
                paginationPerPage={pageSize}
                paginationTotalRows={TotalRecords}
                paginationRowsPerPageOptions={[10, 20, 50, 100, 150, 500, 1000, 1500]}
                onChangePage={(page) => setPage(page)}
                onChangeRowsPerPage={(currentRowsPerPage, currentPage) => { setPageSize(currentRowsPerPage); setPage(currentPage) }}
                sortServer
                onSort={(column, sortDirection) => { setSortBy(column.sortField); setSortOrder(sortDirection); }}
                selectableRows={selectable}
                onSelectedRowsChange={handleRowSelected}
                clearSelectedRows={!selectable}
              />
              )}
            </div>
          </div>
        </div>
      </div>


      <Tooltip id="license-index-edit" style={{ zIndex: 99 }} />
      <Tooltip id="license-index-attached-machines" style={{ zIndex: 99 }} />
      <Tooltip id="license-index-edit-user" style={{ zIndex: 99 }} />
      <Tooltip id="license-index-view-invoice" style={{ zIndex: 99 }} />
      <Tooltip id="license-index-view-user" style={{ zIndex: 99 }} />
      <Tooltip id="license-index-renew" style={{ zIndex: 99 }} />
      <Tooltip id="license-index-renewal-email" style={{ zIndex: 99 }} />
      <Tooltip id="license-index-logs" style={{ zIndex: 99 }} />
      <TruncatedTextTooltip />

      {/* Reseller Renew Modal */}
      <Modal show={showRenewModal} onHide={() => { setShowRenewModal(false); setRenewingLicense(null); setRenewValue(1); setRenewUnit("years"); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Renew License</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renewingLicense && (
            <>
              <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14 }}>
                <div><strong>Ref:</strong> {renewingLicense.license_reference_id}</div>
                <div><strong>Product:</strong> {renewingLicense.product}</div>
                <div><strong>Current Expiry:</strong> {renewingLicense.expires_at ? new Date(renewingLicense.expires_at).toLocaleDateString() : '—'}</div>
                <div><strong>New Expiry:</strong> <span style={{ color: '#523ba8', fontWeight: 600 }}>{addDaysToDate(renewingLicense.expires_at, renewCredits).toLocaleDateString()}</span></div>
              </div>

              <Form.Label>Duration to add</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="number"
                  min={1}
                  value={renewValue}
                  onChange={e => setRenewValue(Math.max(1, parseInt(e.target.value) || 1))}
                  style={{ maxWidth: 110 }}
                />
                <Form.Select
                  value={renewUnit}
                  onChange={e => setRenewUnit(e.target.value)}
                  style={{ flex: 1 }}
                >
                  {DURATION_UNIT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </div>

              <Form.Check
                className="mt-3"
                type="checkbox"
                id="renew-notify-customer"
                label="Email the customer that their license has been renewed"
                checked={renewNotify}
                onChange={e => setRenewNotify(e.target.checked)}
              />

              <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 8, background: '#f0edf8', fontSize: 13 }}>
                <div>Credits needed: <strong>{renewCredits}</strong> <span style={{ color: '#64748b' }}>({renewValue} {renewUnit} × {DAYS_PER_UNIT[renewUnit]} days)</span></div>
                <div>Credits available: <strong>{availableCredits}</strong></div>
                <div>Credits after renewal: <strong style={{ color: availableCredits - renewCredits < 0 ? '#dc3545' : '#016641' }}>{availableCredits - renewCredits}</strong></div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowRenewModal(false); setRenewingLicense(null); setRenewValue(1); setRenewUnit("years"); }}>Cancel</Button>
          <Button
            className="admin-btn"
            onClick={handleRenew}
            disabled={renewing || !renewingLicense || availableCredits < renewCredits}
          >
            {renewing ? 'Renewing…' : 'Renew License'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ActivityLogModal
        show={showActivityModal}
        onHide={() => setShowActivityModal(false)}
        sourceType="Licenses"
        sourceId={selectedLicenseId}
        title="License Activity Logs"
      />
      <CommandLogModal
        show={showCommandModal}
        onHide={() => setShowCommandModal(false)}
        licenseId={selectedCommandLicenseId}
        title="License Command Logs"
      />
    </div>
  );
}
